/**
 * weaponModStats.ts — fold socketed weapon-mod buffs into computed weapon stats.
 *
 * A weapon only receives its mod buffs when it is ARMED (flipped to "Modded" in the
 * loadout screen) AND has ≥1 mod socketed. Standard guns are untouched, so the
 * player can carry a kitted gun clean (no buffs, no risk) by leaving it Standard.
 *
 * Buff numbers come straight from WEAPON_MODS × the per-item rarity multiplier
 * (modBuffValue). These are PLACEHOLDER magnitudes — Dylan tunes them in the
 * Balance Dashboard "🔧 Weapon Mods" tab / a dedicated balance pass (#129).
 *
 * Stat mapping (ModStat → engine stat):
 *   damage     (pct) → damage ×(1 + v/100)
 *   attackSpeed(pct) → reloadTime ÷(1 + v/100)   (faster fire)
 *   crit       (pct) → critChance + v/100         (additive percentage points)
 *   knockback  (pct) → knockback ×(1 + v/100)
 *   pierce     (flat)→ projectilePiercing + v
 *   multishot  (flat)→ bonus SIMULTANEOUS projectiles per shot (see weaponModMultishot;
 *                      NOT projectileCount — that field is the magazine/ammo size).
 */
import { WEAPON_MODS, modBuffValue, type ModCode } from './weaponMods';
import { getEquipped } from './weaponModLoadout';
import { isArmed } from './weaponArmed';

export interface ModdableStats {
  damage: number;
  critChance: number;
  knockback: number;
  projectileCount: number;
  reloadTime: number;
  projectilePiercing: number;
}

/** True if this weapon should run with its mods (armed + at least one socketed). */
export function weaponRunsModded(weaponId: string): boolean {
  return isArmed(weaponId) && getEquipped(weaponId).length > 0;
}

/**
 * Return a copy of `stats` with the weapon's socketed-mod buffs applied, or the
 * same object unchanged when the weapon isn't running modded. Generic over the
 * full stat shape so callers can pass computeWeaponStats' result straight through.
 */
export function applyModBuffs<T extends ModdableStats>(stats: T, weaponId: string): T {
  if (!weaponRunsModded(weaponId)) return stats;

  // Aggregate per-stat buff values (equip() allows at most one mod per code).
  // multishot is intentionally NOT aggregated here — it's a per-shot SIMULTANEOUS
  // projectile bonus handled at the fire site via weaponModMultishot(), not a stat
  // on the weapon (projectileCount is the magazine, so adding to it just grew ammo).
  let dmgPct = 0, firePct = 0, critPct = 0, knockPct = 0, pierceFlat = 0;
  for (const e of getEquipped(weaponId)) {
    const mod = WEAPON_MODS[e.code as ModCode];
    if (!mod) continue;
    const v = modBuffValue(mod, e.rarity);
    switch (mod.stat) {
      case 'damage':      dmgPct += v; break;
      case 'attackSpeed': firePct += v; break;
      case 'crit':        critPct += v; break;
      case 'knockback':   knockPct += v; break;
      case 'pierce':      pierceFlat += v; break;
    }
  }

  return {
    ...stats,
    damage: Math.round(stats.damage * (1 + dmgPct / 100)),
    reloadTime: stats.reloadTime / (1 + firePct / 100),
    critChance: Math.min(1, stats.critChance + critPct / 100),
    knockback: stats.knockback * (1 + knockPct / 100),
    projectilePiercing: stats.projectilePiercing + pierceFlat,
  };
}

/**
 * Flat number of EXTRA simultaneous projectiles a weapon's socketed multishot mod
 * grants (0 when the gun isn't running modded). Added to player.bonusProjectileCount
 * at the fire site so the modded gun fans out extra bullets per shot.
 */
export function weaponModMultishot(weaponId: string): number {
  if (!weaponRunsModded(weaponId)) return 0;
  let n = 0;
  for (const e of getEquipped(weaponId)) {
    const mod = WEAPON_MODS[e.code as ModCode];
    if (mod?.stat === 'multishot') n += modBuffValue(mod, e.rarity);
  }
  return n;
}
